/**
 * Retouren v3 (sessie 172): het retourdossier, de held van de pagina.
 *
 * Maat: Brain grote-ronde/mock-retouren-v3 (desktop rechterhelft en
 * iphone-dossier). Data uit `retourenV3Api.dossier`; elke schrijfroute
 * geeft het nieuwe dossier terug, dat gaat meteen in de cache, en
 * `onGewijzigd` laat de lijst opnieuw lezen.
 *
 * Geen copiloot, geen badges, geen tellers, geen uitlegzinnen.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Link } from "react-router-dom";

import { vertaalApiFout } from "@/lib/api";
import { retourenV3Api, type RetourV3Dossier } from "@/lib/retouren-v3-api";
import { werkplekRetourenApi } from "@/lib/werkplek-api";

import { DossierContact } from "./DossierContact";
import { DossierFeiten } from "./DossierFeiten";
import { DossierKnoppen } from "./DossierKnoppen";
import { TijdlijnV3 } from "./TijdlijnV3";
import "@/styles/statusbalk.css";
import "./dossier-v3.css";

function berichtVeldId(retourId: string): string {
  return `rv3d-bericht-${retourId}`;
}

export function RetourDossierV3(props: {
  retourId: string;
  smal: boolean;
  onTerug?: () => void;
  onGewijzigd?: () => void;
}): JSX.Element {
  const { retourId, smal, onTerug, onGewijzigd } = props;
  const client = useQueryClient();
  const sleutel = ["retouren-v3", "dossier", retourId] as const;

  const vraag = useQuery({
    queryKey: sleutel,
    queryFn: () => retourenV3Api.dossier(retourId),
  });

  const opNieuw = useCallback(
    (nieuw: RetourV3Dossier) => {
      client.setQueryData(["retouren-v3", "dossier", retourId], nieuw);
      onGewijzigd?.();
    },
    [client, retourId, onGewijzigd],
  );

  const verversen = useCallback(() => {
    void client.invalidateQueries({
      queryKey: ["retouren-v3", "dossier", retourId],
      exact: true,
    });
    onGewijzigd?.();
  }, [client, retourId, onGewijzigd]);

  const zorgZaak = useCallback(async (): Promise<string> => {
    const huidig = client.getQueryData<RetourV3Dossier>([
      "retouren-v3",
      "dossier",
      retourId,
    ]);
    if (huidig?.zaak_id) return huidig.zaak_id;
    const { zaak_id } = await werkplekRetourenApi.zaak(retourId);
    if (huidig)
      client.setQueryData(["retouren-v3", "dossier", retourId], {
        ...huidig,
        zaak_id,
      });
    return zaak_id;
  }, [client, retourId]);

  const terugknop = smal ? (
    <button
      type="button"
      className="rv3d-terug"
      aria-label="Terug naar de lijst"
      data-testid="rv3d-terug"
      onClick={() => onTerug?.()}
    >
      {"‹"}
    </button>
  ) : null;

  if (vraag.isLoading || (!vraag.data && !vraag.isError)) {
    return (
      <section
        aria-label="Retourdossier"
        className={smal ? "rv3d rv3d-smal" : "rv3d rv3d-breed"}
        aria-busy="true"
      >
        {smal ? (
          <header className="rv3d-smal-kop vo-statusbalkvrij">
            <div className="rv3d-smal-kopregel">{terugknop}</div>
          </header>
        ) : null}
        <div className="rv3d-melding" style={smal ? { padding: "18px 20px" } : undefined}>
          Bezig met laden…
        </div>
      </section>
    );
  }

  if (vraag.isError || !vraag.data) {
    return (
      <section
        aria-label="Retourdossier"
        className={smal ? "rv3d rv3d-smal" : "rv3d rv3d-breed"}
      >
        {smal ? (
          <header className="rv3d-smal-kop vo-statusbalkvrij">
            <div className="rv3d-smal-kopregel">{terugknop}</div>
          </header>
        ) : null}
        <div className="rv3d-fout" role="alert" style={smal ? { padding: "18px 20px" } : undefined}>
          {vertaalApiFout(vraag.error, "Deze retour kon niet worden geladen.")}
        </div>
      </section>
    );
  }

  const dossier = vraag.data;
  // Sessie 186: het retournummer staat bovenaan, af te lezen en te kopiëren.
  const nummer = dossier.retournummer ? (
    <span className="rv3d-retournummer" data-testid="rv3d-retournummer">
      Retour {dossier.retournummer}
    </span>
  ) : null;
  const klantkaart = dossier.klant_id ? (
    <Link className="rv3d-kopknop" to={`/klanten/${dossier.klant_id}`}>
      Klantkaart
    </Link>
  ) : null;

  const contact = (
    <DossierContact
      dossier={dossier}
      smal={smal}
      veldId={berichtVeldId(retourId)}
      zorgZaak={zorgZaak}
      verversen={verversen}
    />
  );

  const knoppen = (
    <DossierKnoppen
      dossier={dossier}
      smal={smal}
      opNieuw={opNieuw}
      verversen={verversen}
      zorgZaak={zorgZaak}
      onMail={
        smal
          ? () => {
              const veld = document.getElementById(berichtVeldId(retourId));
              veld?.scrollIntoView?.({ block: "center", behavior: "smooth" });
              veld?.focus();
            }
          : undefined
      }
    />
  );

  if (smal) {
    return (
      <section
        aria-label="Retourdossier"
        className="rv3d rv3d-smal"
        data-testid="rv3d"
        data-vorm="smal"
      >
        <header className="rv3d-smal-kop vo-statusbalkvrij">
          <div className="rv3d-smal-kopregel">
            {terugknop}
            <div className="rv3d-kopregel">
              {nummer}
              <span>{dossier.kopregel_kort}</span>
            </div>
            {klantkaart}
          </div>
          <h1 className="rv3d-smal-standzin">{dossier.stand_zin}</h1>
        </header>
        <div className="rv3d-smal-inhoud">
          <TijdlijnV3 stappen={dossier.tijdlijn} smal />
          <DossierFeiten dossier={dossier} smal opNieuw={opNieuw} />
          {contact}
          {knoppen}
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Retourdossier"
      className="rv3d rv3d-breed"
      data-testid="rv3d"
      data-vorm="breed"
    >
      <div className="rv3d-kop">
        <div>
          <div className="rv3d-kopregel">
            {nummer}
            <span>{dossier.kopregel}</span>
          </div>
          <h2 className="rv3d-standzin">{dossier.stand_zin}</h2>
        </div>
        <div className="rv3d-kopknoppen">
          {klantkaart}
          {dossier.lightspeed_order_url ? (
            <a
              className="rv3d-kopknop"
              href={dossier.lightspeed_order_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Lightspeed
            </a>
          ) : null}
        </div>
      </div>
      <TijdlijnV3 stappen={dossier.tijdlijn} smal={false} />
      <DossierFeiten dossier={dossier} smal={false} opNieuw={opNieuw} />
      {knoppen}
      {contact}
    </section>
  );
}
